import Autocomplete from '../AutocompleteBase';
import type { IFindAutocompleteItem } from './models/find-autocomplete-item.model';
import FindAutocompleteApiModel from './models/find-autocomplete.api-model';
import './style.scss';
import type { IAutocompleteItem } from '../../../models/autocomplete-item.model';
import { AutocompleteSuggestionSource, Panels } from '../AutocompleteBase/autocomplete-base.enum';
import { JSX } from 'react';
import { useTexts } from '../../../hooks/texts.hook';

export interface ISearchAutocompleteState {
  value: {
    selectedItem: IFindAutocompleteItem | undefined;
    searchText: string;
    preventSearch: boolean;
  };
}

interface ISearchAutocompleteProps {
  id: string;
  labelText?: string;
  placeholderText?: string;
  className?: string;
  state: ISearchAutocompleteState;
  inputName?: string;
  positionRelative?: boolean;
  onSelect?: (item: IFindAutocompleteItem | undefined) => void;
  onSearchTextChange?: (newSearchText: string) => void;
  onPanelsChange?: (newPanel: Panels) => void;
  onFocus?: (() => void) | undefined;
}

function createResourceUrl(searchText: string): string {
  return `/find_v2/_autocomplete?size=5&prefix=${searchText}`;
}

function processReceivedData(data: unknown | undefined): IAutocompleteItem[] | undefined {
  return data ? FindAutocompleteApiModel.mapToBusinessModel(data as FindAutocompleteApiModel) : undefined;
}

function getSuggestionId(componentId: string, item: IAutocompleteItem, index: number): string | undefined {
  if (!item) return undefined;
  return `autocomplete-result-item-${componentId}-${index}`;
}

function getItemId(item: IAutocompleteItem): string | undefined {
  return (item as IFindAutocompleteItem)?.query;
}

function getDisplayName(item: IAutocompleteItem | undefined): string | undefined {
  return (item as IFindAutocompleteItem)?.query;
}

function createSuggestionElement(suggestionId: string, item: IAutocompleteItem, isActive: boolean, _suggestionSource: AutocompleteSuggestionSource, onClick: () => void): JSX.Element {
  return (
    <li
      id={suggestionId}
      key={suggestionId}
      role="option"
      aria-selected={isActive}
      className={`autocomplete-result d-flex align-items-center py-2 px-2 m-0 w-100 border-x border-top ${isActive ? 'is-active' : ''}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
    >
      <span className="d-block text-truncate">{getDisplayName(item)}</span>
    </li>
  );
}

function SearchAutocomplete({
  id,
  labelText,
  placeholderText,
  className = undefined,
  state,
  inputName = undefined,
  positionRelative = true,
  onSelect = () => {},
  onSearchTextChange,
  onPanelsChange = undefined,
  onFocus,
}: ISearchAutocompleteProps) {
  const descriptionId = `${id}-description`;
  const { t } = useTexts();

  return (
    <div className="flex-grow-1 search-autocomplete">
      <label htmlFor={id} className="visually-hidden">
        {labelText}
      </label>
      <Autocomplete
        selectedItem={state.value.selectedItem}
        id={id}
        searchText={state.value.searchText}
        label={undefined}
        ariaDescribedBy={descriptionId}
        placeholderText={placeholderText}
        showNoResultsText={false}
        inputName={inputName}
        preventSearch={state.value.preventSearch}
        className={`search-autocomplete__input rounded-2 border border-gray-700 ${className}`}
        positionRelative={positionRelative}
        createResourceUrl={createResourceUrl}
        processReceivedData={processReceivedData}
        getSuggestionId={getSuggestionId}
        getItemId={getItemId}
        getDisplayName={getDisplayName}
        createSuggestionElement={createSuggestionElement}
        onSelect={(x) => {
          onSelect(x as IFindAutocompleteItem);
        }}
        onSearchTextChange={onSearchTextChange}
        onPanelsChange={onPanelsChange}
        onFocus={onFocus}
      />
      <div id={descriptionId} className="visually-hidden">
        {t('Skriv in vad du vill söka på', 'Write what you want to search for')}
      </div>
    </div>
  );
}

export default SearchAutocomplete;
